import React, { useEffect, useRef, useState } from 'react'
import { Button, Input, Modal, Tooltip } from 'antd'
import { CommentOutlined, SendOutlined, UserOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import { AppDispatch } from '../../../../redux/store'
import { setTriggerTime } from '../../../../redux/ducks/commonDucks'
import { handleError, showPrice } from '../../../../utils/commonUtils'
import { getUserInfo } from '../../../../utils/userUtils'
import * as purchasesRequestApi from '../../../../api/purchasesRequestApi'
import {
  buildChat,
  CmtSide,
  findHighlightChatIndex,
  formatCmtPreviewLines,
  formatMixedChatPreviewLines,
} from '../purchasesRequestChatUtils'
import './PurchasesRequestCmtChatCells.css'

const formatCmtTime = (t: string) => (t ? moment(t).format('DD/MM/YYYY HH:mm') : '')

const formatYcmhDate = (v: string | number | null | undefined) => {
  if (v == null || v === '') return '—'
  const m = moment(String(v), ['YYYYMMDD', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD/MM/YYYY') : String(v)
}

const formatYcmhDateTime = (v: string | null | undefined) => {
  if (!v) return '—'
  const m = moment(v)
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : String(v)
}

const dash = (v: any) => {
  if (v == null || v === '') return '—'
  return String(v)
}

const styles = {
  modalBody: { padding: 0 } as React.CSSProperties,
  shell: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: 'calc(100vh - 130px)',
    maxHeight: 'calc(100vh - 130px)',
  },
  headerSub: {
    fontSize: 12,
    fontWeight: 400,
    color: 'rgba(0,0,0,0.45)',
    marginTop: 2,
  },
  infoPanel: {
    borderBottom: '1px solid #f0f0f0',
    background: '#fafcff',
    padding: '10px 16px 12px',
    flexShrink: 0,
    maxHeight: 220,
    overflowY: 'auto' as const,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px 16px',
    fontSize: 12,
    lineHeight: 1.4,
  },
  infoFull: {
    gridColumn: '1 / -1' as const,
  },
  infoLabel: {
    color: '#8c8c8c',
    marginRight: 6,
    whiteSpace: 'nowrap' as const,
  },
  infoValue: {
    color: 'rgba(0,0,0,0.85)',
    wordBreak: 'break-word' as const,
  },
  thread: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '16px 16px 12px',
    background: 'linear-gradient(180deg, #f4f7fb 0%, #eef2f7 100%)',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#8c8c8c',
    gap: 8,
  },
  row: (mine: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: mine ? 'flex-end' : 'flex-start',
    gap: 8,
    marginBottom: 12,
  }),
  avatar: (mine: boolean): React.CSSProperties => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: mine ? '#1890ff' : '#595959',
  }),
  bubble: (mine: boolean, highlight?: boolean): React.CSSProperties => ({
    maxWidth: '72%',
    background: highlight ? '#fff1f0' : mine ? '#1890ff' : '#fff',
    color: highlight ? '#cf1322' : mine ? '#fff' : 'rgba(0,0,0,0.85)',
    borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
    padding: '8px 12px',
    fontSize: 13,
    lineHeight: 1.45,
    border: highlight ? '1px solid #ff4d4f' : undefined,
    boxShadow: highlight
      ? '0 0 0 2px rgba(255,77,79,0.25)'
      : mine
        ? '0 2px 8px rgba(24,144,255,0.25)'
        : '0 1px 4px rgba(0,0,0,0.08)',
  }),
  meta: (mine: boolean, highlight?: boolean): React.CSSProperties => ({
    fontSize: 11,
    marginBottom: 3,
    opacity: highlight ? 1 : mine ? 0.85 : 1,
    color: highlight ? '#a8071a' : mine ? 'rgba(255,255,255,0.85)' : '#8c8c8c',
  }),
  composer: {
    borderTop: '1px solid #f0f0f0',
    padding: '12px 14px',
    background: '#fff',
  },
  composerInner: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-end',
  },
  hint: {
    fontSize: 12,
    color: '#8c8c8c',
    textAlign: 'center' as const,
    padding: '14px 16px',
    background: '#fafafa',
    borderTop: '1px solid #f0f0f0',
  },
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode; full?: boolean }> = ({
  label,
  value,
  full,
}) => (
  <div style={full ? styles.infoFull : undefined}>
    <span style={styles.infoLabel}>{label}:</span>
    <span style={styles.infoValue}>{value}</span>
  </div>
)

/** Khối thông tin YCMH phía trên đoạn chat */
const YcmhChatInfoPanel: React.FC<{ ycmh: any }> = ({ ycmh }) => {
  const maSp = ycmh?.sanPham?.ma ?? ycmh?.maSanPham
  const tenSp = ycmh?.sanPham?.ten ?? ycmh?.tenSanPham
  const khoVn = ycmh?.khoVN?.ten ?? ycmh?.khoVN?.ma ?? ycmh?.khoVN
  const soLuong = ycmh?.soLuong
  const donGia = ycmh?.donGiaYeuCauVND
  const donGiaText =
    donGia == null || donGia === ''
      ? '—'
      : `${showPrice(Number(donGia))}đ`

  return (
    <div style={styles.infoPanel}>
      <div style={styles.infoGrid}>
        <InfoRow label="Mã SP" value={dash(maSp)} />
        <InfoRow label="Kho VN" value={dash(khoVn)} />
        <InfoRow label="Tên SP" value={dash(tenSp)} full />
        <InfoRow
          label="Số lượng"
          value={soLuong == null || soLuong === '' ? '—' : showPrice(Number(soLuong))}
        />
        <InfoRow label="Đơn giá YC" value={donGiaText} />
        <InfoRow label="Ngày nhận hàng" value={formatYcmhDate(ycmh?.ngayYeuCauNhanHangKhoVN)} />
        <InfoRow label="Ngày tạo" value={formatYcmhDateTime(ycmh?.createdAt)} />
        <InfoRow label="Mô tả" value={dash(ycmh?.moTaChiTietHangHoa)} full />
        <InfoRow label="Ghi chú" value={dash(ycmh?.ghiChu)} full />
      </div>
    </div>
  )
}

/** Text cột list: hiện text, "Xem thêm" / "Thu gọn" nếu dài */
const COLLAPSED_LINE_COUNT = 3

export const PurchasesRequestChatTextCell: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false)
  const full = String(text || '').trim()
  if (!full) return <span style={{ color: '#bfbfbf' }}>—</span>

  const lines = full.split('\n')
  const needMore = lines.length > COLLAPSED_LINE_COUNT || full.length > 160
  const shown =
    expanded || !needMore ? full : lines.slice(0, COLLAPSED_LINE_COUNT).join('\n')

  return (
    <div
      style={{
        fontSize: 12,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        textAlign: 'left',
        color: 'rgba(0,0,0,0.85)',
      }}
    >
      {shown}
      {needMore ? (
        <div>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontSize: 12 }}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Thu gọn' : 'Xem thêm'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/** Preview 1 phía (KD hoặc MH) trên list */
export const PurchasesRequestCmtPreviewCell: React.FC<{ raw: string | null | undefined }> = ({
  raw,
}) => {
  const lines = formatCmtPreviewLines(raw, (t) => (t ? moment(t).format('DD/MM HH:mm') : ''))
  return <PurchasesRequestChatTextCell text={lines.join('\n')} />
}

/** Preview đoạn chat mix MH+KD */
export const PurchasesRequestMixedChatTextCell: React.FC<{
  cmtMuaHang: string | null | undefined
  cmtKinhDoanh: string | null | undefined
}> = ({ cmtMuaHang, cmtKinhDoanh }) => {
  const lines = formatMixedChatPreviewLines(
    cmtMuaHang,
    cmtKinhDoanh,
    (t) => (t ? moment(t).format('DD/MM HH:mm') : ''),
  )
  return <PurchasesRequestChatTextCell text={lines.join('\n')} />
}

/** 1 cột Chat: nút mở modal (MH / KD chat, chỉ đúng phía trên đơn mới gửi được) */
export const PurchasesRequestChatCell: React.FC<{
  record: any
  /** Hiện chữ cạnh icon (vd. màn báo giá) */
  showLabel?: boolean
  label?: string
  /** Mở modal ngay (vd. click thông báo) */
  autoOpen?: boolean
  /** Ẩn nút trigger, chỉ giữ modal */
  hideTrigger?: boolean
  /** Nội dung tin nhắn cần tô đỏ (từ thông báo vừa click) */
  highlightCmt?: string | null
  /** Thời gian thông báo — dùng khi trùng nội dung */
  highlightAt?: string | null
  onClosed?: () => void
}> = ({
  record,
  showLabel = false,
  label = 'Chat',
  autoOpen = false,
  hideTrigger = false,
  highlightCmt = null,
  highlightAt = null,
  onClosed,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [ycmh, setYcmh] = useState<any>(record)
  const [cmtMuaHang, setCmtMuaHang] = useState(record?.cmtMuaHang)
  const [cmtKinhDoanh, setCmtKinhDoanh] = useState(record?.cmtKinhDoanh)
  const [needListRefresh, setNeedListRefresh] = useState(false)
  const threadRef = useRef<HTMLDivElement>(null)
  const highlightMsgRef = useRef<HTMLDivElement>(null)
  const dirtyWhileOpen = useRef(false)
  const openRef = useRef(false)
  const reloadSeq = useRef(0)
  const didAutoOpen = useRef(false)
  const didScrollHighlight = useRef(false)

  useEffect(() => {
    if (open) return
    setYcmh(record)
    setCmtMuaHang(record?.cmtMuaHang)
    setCmtKinhDoanh(record?.cmtKinhDoanh)
  }, [record, open])

  const userInfo = getUserInfo() || {}
  const userId = Number(userInfo.id)
  const isMh = userId === Number(ycmh?.nhanVienMuaHangId ?? ycmh?.nhanVienMuaHang?.id)
  const isKd = userId === Number(ycmh?.nhanVienYeuCauId ?? ycmh?.nhanVienYeuCau?.id)
  const canSend = isMh || isKd
  const sendSide: CmtSide | null = isKd ? 'KD' : (isMh ? 'MH' : null)

  const chat = buildChat(cmtMuaHang, cmtKinhDoanh)
  const highlightIdx = findHighlightChatIndex(chat, highlightCmt, highlightAt)
  const unreadHint = chat.length > 0 ? ` (${chat.length})` : ''
  const mhName = ycmh?.nhanVienMuaHang?.username || '—'
  const kdName = ycmh?.nhanVienYeuCau?.username || '—'

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (!openRef.current) return
      const el = threadRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
  }

  const scrollToHighlight = () => {
    requestAnimationFrame(() => {
      if (!openRef.current) return
      highlightMsgRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }

  const applyCmtFromPayload = (data: any) => {
    if (!data || !openRef.current) return
    if (data.cmtMuaHang != null) setCmtMuaHang(data.cmtMuaHang)
    if (data.cmtKinhDoanh != null) setCmtKinhDoanh(data.cmtKinhDoanh)
  }

  const reloadChat = async (opts?: { silent?: boolean }) => {
    if (!record?.id || !openRef.current) return
    const seq = ++reloadSeq.current
    const silent = !!opts?.silent
    try {
      if (!silent) setRefreshing(true)
      const res: any = await purchasesRequestApi.getDetailPurchasesRequest(String(record.id))
      // Đã đóng modal / có request mới hơn → bỏ qua, không set state
      if (!openRef.current || seq !== reloadSeq.current) return
      const detail = res?.data ?? res
      if (detail && typeof detail === 'object') {
        setYcmh((prev: any) => ({ ...prev, ...detail }))
        applyCmtFromPayload(detail)
      }
      if (highlightCmt && !didScrollHighlight.current) {
        // scroll sau khi state cập nhật — effect bên dưới sẽ xử lý
      } else if (!highlightCmt) {
        scrollToBottom()
      }
    } catch (err) {
      if (!openRef.current || seq !== reloadSeq.current) return
      if (!silent) handleError(err)
    } finally {
      if (seq === reloadSeq.current) setRefreshing(false)
    }
  }

  // Poll 10s khi modal mở; clear khi đóng (không load tiếp sau close)
  useEffect(() => {
    if (!open) return
    const timer = window.setInterval(() => {
      if (!openRef.current) return
      reloadChat({ silent: true })
    }, 10_000)
    return () => {
      window.clearInterval(timer)
      reloadSeq.current += 1 // invalidate in-flight
    }
  }, [open, record?.id])

  useEffect(() => {
    if (!open) return
    if (highlightIdx >= 0) {
      didScrollHighlight.current = true
      scrollToHighlight()
      return
    }
    if (!highlightCmt) scrollToBottom()
  }, [open, chat.length, highlightIdx, highlightCmt])

  const onOpen = async () => {
    openRef.current = true
    didScrollHighlight.current = false
    setYcmh(record)
    setOpen(true)
    dirtyWhileOpen.current = false
    setNeedListRefresh(false)
    await reloadChat()
  }

  const onClose = () => {
    openRef.current = false
    reloadSeq.current += 1 // hủy kết quả request đang bay
    setRefreshing(false)
    setOpen(false)
    setValue('')
    didScrollHighlight.current = false
    if (dirtyWhileOpen.current || needListRefresh) {
      dispatch(setTriggerTime(+new Date()))
      dirtyWhileOpen.current = false
      setNeedListRefresh(false)
    }
    onClosed?.()
  }

  useEffect(() => {
    if (!autoOpen || !record?.id || didAutoOpen.current) return
    didAutoOpen.current = true
    onOpen()
  }, [autoOpen, record?.id])

  const onSend = async () => {
    const noiDung = value.trim()
    if (!noiDung || !record?.id || !sendSide || loading || !openRef.current) return
    try {
      setLoading(true)
      const res: any = sendSide === 'MH'
        ? await purchasesRequestApi.appendCmtMuaHang(record.id, noiDung)
        : await purchasesRequestApi.appendCmtKinhDoanh(record.id, noiDung)
      if (!openRef.current) return
      const data = res?.data ?? res
      applyCmtFromPayload(data)
      await reloadChat()
      if (!openRef.current) return
      setValue('')
      dirtyWhileOpen.current = true
      setNeedListRefresh(true)
      scrollToBottom()
    } catch (err) {
      if (openRef.current) handleError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="textCenter">
      {!hideTrigger && (
        <Tooltip
          title={
            canSend
              ? `Chat${unreadHint}`
              : 'Chỉ NV mua hàng hoặc NV yêu cầu (sale) trên đơn mới gửi được comment.'
          }
        >
          <span style={{ display: 'inline-block' }}>
            <Button
              size="small"
              type="primary"
              icon={<CommentOutlined />}
              onClick={onOpen}
              disabled={!canSend}
            >
              {showLabel ? label : (chat.length > 0 ? chat.length : null)}
              {showLabel && chat.length > 0 ? ` (${chat.length})` : null}
            </Button>
          </span>
        </Tooltip>
      )}

      <Modal
        visible={open}
        onCancel={onClose}
        footer={null}
        width={680}
        destroyOnClose={false}
        bodyStyle={styles.modalBody}
        style={{ top: 20, paddingBottom: 20 }}
        wrapClassName="ycmh-chat-modal"
        title={
          <div style={{ paddingRight: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: '#e6f7ff',
                  color: '#1890ff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CommentOutlined />
              </span>
              <span>Chat YCMH · {ycmh?.ma || record?.ma || ''}</span>
            </div>
            <div style={styles.headerSub}>
              MH: {mhName} &nbsp;·&nbsp; KD: {kdName}
              {refreshing ? ' · Đang tải...' : ''}
            </div>
          </div>
        }
      >
        <div style={styles.shell}>
          <YcmhChatInfoPanel ycmh={ycmh} />

          <div ref={threadRef} style={styles.thread}>
            {chat.length === 0 ? (
              <div style={styles.empty}>
                <CommentOutlined style={{ fontSize: 36, color: '#bfbfbf' }} />
                <div>Chưa có tin nhắn</div>
                <div style={{ fontSize: 12 }}>Gửi comment đầu tiên cho đơn này</div>
              </div>
            ) : (
              chat.map((item, idx) => {
                const isMhMsg = item.side === 'MH'
                const mine =
                  (sendSide === 'MH' && isMhMsg) || (sendSide === 'KD' && !isMhMsg)
                const highlight = idx === highlightIdx
                const username =
                  item.u ||
                  (isMhMsg
                    ? ycmh?.nhanVienMuaHang?.username
                    : ycmh?.nhanVienYeuCau?.username) ||
                  (isMhMsg ? 'Mua hàng' : 'Kinh doanh')
                const initial = String(username).charAt(0).toUpperCase() || '?'
                return (
                  <div
                    key={`${item.t}-${idx}`}
                    ref={highlight ? highlightMsgRef : undefined}
                    style={styles.row(mine)}
                  >
                    {!mine && <div style={styles.avatar(false)}>{initial}</div>}
                    <div style={styles.bubble(mine, highlight)}>
                      <div style={styles.meta(mine, highlight)}>
                        {username} · {isMhMsg ? 'MH' : 'KD'} · {formatCmtTime(item.t)}
                        {highlight ? ' · Từ thông báo' : ''}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.c}</div>
                    </div>
                    {mine && <div style={styles.avatar(true)}>{initial}</div>}
                  </div>
                )
              })
            )}
          </div>

          {canSend && sendSide ? (
            <div style={styles.composer}>
              <div style={styles.composerInner}>
                <Input.TextArea
                  value={value}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  placeholder={
                    sendSide === 'KD'
                      ? 'Kinh doanh nhập comment... (Enter để gửi)'
                      : 'Mua hàng nhập comment... (Enter để gửi)'
                  }
                  onChange={(e) => setValue(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault()
                      onSend()
                    }
                  }}
                  disabled={loading}
                  style={{ borderRadius: 10, resize: 'none' }}
                />
                <Button
                  type="primary"
                  loading={loading}
                  icon={<SendOutlined />}
                  onClick={onSend}
                  disabled={!value.trim()}
                  style={{ height: 40, borderRadius: 10, minWidth: 44 }}
                />
              </div>
            </div>
          ) : (
            <div style={styles.hint}>
              <UserOutlined style={{ marginRight: 6 }} />
              Chỉ NV mua hàng hoặc NV yêu cầu (sale) trên đơn mới gửi được comment.
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

/** Mở chat YCMH theo id (từ thông báo chuông) */
export const PurchasesRequestChatByIdHost: React.FC<{
  yeuCauMuaId: string | number | null
  highlightCmt?: string | null
  highlightAt?: string | null
  onClose: () => void
}> = ({ yeuCauMuaId, highlightCmt = null, highlightAt = null, onClose }) => {
  const [record, setRecord] = useState<any>(null)

  useEffect(() => {
    let cancelled = false
    if (yeuCauMuaId == null || yeuCauMuaId === '') {
      setRecord(null)
      return
    }
    const loadDetail = async () => {
      try {
        const res: any = await purchasesRequestApi.getDetailPurchasesRequest(String(yeuCauMuaId))
        if (cancelled) return
        setRecord(res?.data ?? res)
      } catch (err) {
        if (!cancelled) {
          handleError(err)
          onClose()
        }
      }
    }
    loadDetail()
    return () => {
      cancelled = true
    }
  }, [yeuCauMuaId])

  if (yeuCauMuaId == null || yeuCauMuaId === '' || !record?.id) return null

  return (
    <PurchasesRequestChatCell
      key={`${yeuCauMuaId}-${highlightCmt || ''}-${highlightAt || ''}`}
      record={record}
      autoOpen
      hideTrigger
      highlightCmt={highlightCmt}
      highlightAt={highlightAt}
      onClosed={onClose}
    />
  )
}
